import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import EmailNoticeBanner from "@/components/EmailNoticeBanner";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  // Tap redirects with ?tap_id=chg_... ; keep session_id as a fallback alias.
  const sessionId = params.get("tap_id") || params.get("session_id") || params.get("charge_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ stage: "loading", info: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ stage: "error", info: "معرّف الجلسة غير موجود" });
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ stage: "paid", info: data });
          return;
        }
        if (data.status === "expired") {
          setState({ stage: "expired", info: data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 12) {
          setState({ stage: "timeout", info: data });
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        setState({ stage: "error", info: e.response?.data?.detail || "خطأ" });
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  return (
    <PageShell>
      <section className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="checkout-success-page">
        {state.stage === "loading" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#0A4D3C]" />
            <h1 className="font-heading text-3xl text-[#222222] mt-6">جاري التحقق من الدفع...</h1>
            <p className="text-[#5E5C58] mt-2">قد يستغرق ذلك بضع ثوانٍ.</p>
          </>
        )}
        {state.stage === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center">
              <Check className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="font-heading text-4xl text-[#222222] mt-6" data-testid="payment-success-title">
              {state.info?.purpose === "session_upgrade"
                ? "تمت ترقية جلستك بنجاح"
                : "تمّت عملية الدفع بنجاح"}
            </h1>
            <p className="text-[#5E5C58] mt-3">
              {state.info?.purpose === "session_upgrade"
                ? "تم تحديث جلستك إلى الخدمة والموعد الجديدين. يمكنك مراجعة التفاصيل من حسابك."
                : state.info?.booking_id
                ? "تم تأكيد حجزك. يمكنك مراجعة التفاصيل من حسابك."
                : "تم تأكيد طلبك. يمكنك تحميل الكتاب الآن من حسابك."}
            </p>
            {state.info?.booking_id && (
              <div
                data-testid="checkout-botim-note"
                className="mt-6 mx-auto max-w-md text-start rounded-2xl bg-[#F6F4EE] border border-[#E5E1D8] p-5 leading-relaxed text-[#222222]"
              >
                الجلسة عبر برنامج البوتيم. فضلًا تحميل برنامج البوتيم وتفعيل المكالمات مع إضافة
                رقم التواصل: <strong dir="ltr">00966538278484</strong> وتجربة التطبيق قبل موعد
                الجلسة حتى لا يتم التأجيل 🌹
              </div>
            )}
            <EmailNoticeBanner className="mt-6 mx-auto max-w-md text-start" />
            <Button
              data-testid="go-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="mt-8 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-8 h-11"
            >
              الذهاب إلى حسابي
            </Button>
          </>
        )}
        {(state.stage === "expired" || state.stage === "error" || state.stage === "timeout") && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 grid place-items-center">
              <AlertCircle className="w-8 h-8 text-rose-700" />
            </div>
            <h1 className="font-heading text-3xl text-[#222222] mt-6">
              {state.stage === "expired" ? "انتهت صلاحية الجلسة" : "تعذّر تأكيد الدفع"}
            </h1>
            <p className="text-[#5E5C58] mt-2">
              {typeof state.info === "string" ? state.info : "يرجى المحاولة مرة أخرى من الخدمات أو الكتاب."}
            </p>
            <Button
              onClick={() => navigate("/services")}
              className="mt-8 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-8 h-11"
            >
              العودة للخدمات
            </Button>
          </>
        )}
      </section>
    </PageShell>
  );
};

export default CheckoutSuccess;
